import React, { Fragment, useContext } from "react";
import { Button, Card } from "react-bootstrap";
import { Cell, Filters, Row } from "react-table";
import AbpnSpinner from "../Shared/Components/AbpnSpinner";
import HeaderWithTitle from "../Shared/Components/HeaderWithTitle";
import {
  SelectColumnFilter,
  PreCertTable,
} from "../Shared/Components/PreCertTable";
import Resident, { ApplicationStatus } from "../Residents/Models/Resident";
import { getDateWithOffset } from "../Shared/Utility/GetDateWithOffset";
import { Link, useNavigate } from "react-router-dom";
import { ProfileContext } from "../Auth/ProfileProvider";
import { TrainingProgramTypes } from "../Shared/Models/TrainingProgram";
import { KnownCertificationType } from "../Training/Models/EligibilityRequirements";
import { ActiveRosterContext } from "../Contexts/ActiveRosterProvider";

export const ActiveRoster = () => {
  const { user } = useContext(ProfileContext);
  const { roster, filteredRoster, filters, isRosterLoading, updateFilteredRoster, updateFilters } = useContext(ActiveRosterContext);

  const navigate = useNavigate();
  let userProgramTypeId = user.trainingPrograms.find(
    (_) => _.trainingProgramId === user.selectedTrainingProgramId
  ).trainingProgramTypeId;

  const onFiltersChanged = (
    rows: Array<Row<Resident>>,
    filters: Filters<object>
  ) => {
    var residents = rows.map((_) => {
      return _.original;
    });
    updateFilteredRoster(residents);
    updateFilters(filters);
  };

  const openNewResidentForm = () => {
    navigate("New");
  };

  const getApplicationStatus = (applicationStatus: ApplicationStatus) => {
    switch (applicationStatus) {
      case ApplicationStatus.Ineligable:
        return "No";
      case ApplicationStatus.Eligable:
        return "Yes";
      case ApplicationStatus.EligableNoExam:
        return "Yes";
      case ApplicationStatus.Applied:
        return "Applied";

      default:
        throw new Error("Enumeration value out of range.");
    }
  };

  const showAdditionalStatus = (resident: Resident) =>
    userProgramTypeId === TrainingProgramTypes.Cap &&
    resident.additionalFastTrackApplicationStatus !== null &&
    !resident.trainingAttestations.some(ta =>
      ta.isAttestedComplete &&
      ta.certifications?.some(c => c.certificationId === KnownCertificationType.Psychiatry));

  const columns = React.useMemo(
    () => [
      {
        Header: "First",
        width: "auto",
        filterable: true,
        sortable: true,
        filterType: "fuzzyText",
        accessor: (resident: Resident) => resident.name.firstName,
        id: "arFirstName",
      },
      {
        Header: "Middle",
        width: "auto",
        filterable: true,
        sortable: true,
        filter: "fuzzyText",
        accessor: (resident: Resident) => resident.name.middleName,
        id: "arMiddleName",
      },
      {
        Header: "Last",
        width: "auto",
        filterable: true,
        sortable: true,
        filter: "fuzzyText",
        accessor: (resident: Resident) => resident.name.lastName,
        id: "arLastName",
      },
      {
        Header: "Date Of Birth",
        width: "150px",
        filterable: true,
        sortable: true,
        filter: "fuzzyDate",
        accessor: (resident: Resident) =>
          getDateWithOffset(resident.dateOfBirth).valueOf(),
        Cell: (a: Cell<Resident>) => (
          <div className="text-end">
            {getDateWithOffset(
              a.row.original.dateOfBirth
            ).toLocaleDateString()}
          </div>
        ),
        id: "arDob",
      },
      {
        Header: "SSN4",
        width: "100px",
        filterable: true,
        sortable: true,
        filter: "fuzzyText",
        accessor: (resident: Resident) => resident.ssn4,
        Cell: (a: Cell<Resident>) => (
          <div className="text-end">{a.row.original.ssn4}</div>
        ),
        id: "arSSN4",
      },
      {
        Header: "PGY",
        width: "90px",
        filterable: true,
        sortable: true,
        Filter: SelectColumnFilter,
        filter: "includes",
        accessor: (resident: Resident) =>
          resident.currentPostGraduateYear?.toString() ?? "None",
        Cell: (a: Cell<Resident>) => (
          <div className="text-end">
            {a.row.original.currentPostGraduateYear?.toString() ?? "None"}
          </div>
        ),
        id: "arPgy",
      },
      {
        Header: "Eligible to Apply When Applications Open",
        HeaderInfo: "Your residents will show yes once the application periods are open and you have all of the required information entered into the system.",
        width: "185px",
        filterable: true,
        sortable: true,
        Filter: SelectColumnFilter,
        filter: "includes",
        accessor: (resident: Resident) =>
          getApplicationStatus(resident.applicationStatus),
        Cell: (a: Cell<Resident>) => {
          return (
            <div>
              {showAdditionalStatus(a.row.original) && <><span className="pe-2">P: {getApplicationStatus(a.row.original.additionalFastTrackApplicationStatus)}</span> CAP: </>}
              {getApplicationStatus(a.row.original.applicationStatus)}
            </div >
          );
        },
        id: "arApplicationStatus",
      },
      {
        id: "arActions",
        width: "auto",
        Cell: (a: Cell<Resident>) => {
          return (
            <div>
              <Link
                className="link-primary me-3"
                to={`${a.row.original.physicianId}`}
              >
                View Details
              </Link>
            </div>
          );
        },
      },
    ],
    []
  );

  const residentsMessage = roster && filteredRoster &&
    (filteredRoster.length < roster.length ?
      `${filteredRoster.length} of `
      : ""
    ) + `${roster?.length ?? 0} residents`;

  if (isRosterLoading) return <AbpnSpinner />;
  const activeRoster =
    roster?.length > 0 ? (
      <PreCertTable
        columns={columns}
        data={roster}
        defaultPageSize={15}
        onFiltersChanged={onFiltersChanged}
        defaultFilters={filters}
      />
    ) : (
      <span>No Residents</span>
    );

  return (
    <Fragment>
      <div className="d-flex justify-content-between">
        <HeaderWithTitle title={"Active Roster"} subTitle={""} />
        <div className="d-flex flex-column">
          <Button
            variant="primary"
            size="sm"
            className="pc-button"
            onClick={() => openNewResidentForm()}
          >
            Add New Resident
          </Button>
          <Card.Text className="align-self-end fw-bolder mt-2">
            {residentsMessage}
          </Card.Text>
        </div>
      </div>
      {activeRoster}
    </Fragment>
  );
}
